// @ts-nocheck
/*
 * Create / Edit Agent — v1 logic preserved end-to-end (dual-mode via
 * location.state, agent name/username validators, full password rule set,
 * email OTP verification gated by ACL flag, extension confirmation modal,
 * "Create default extension" auto-generate + editable local, call-centre-admin
 * AgentAccountSettingsSection, demo-company defaults).
 *
 * Chrome swap: react-bootstrap Row/Col/Card/Form.* /Modal/Button → shadcn
 * Card + Dialog + native <select>/<input>/<radio> with form-control CSS
 * (kept because the icon-overlay layout depends on it). react-select and
 * AgentAccountSettingsSection stay as-is.
 */
import { canUseEarlyFollowUp } from '@/utilities/dispositionFormsAcl';
import {
  AvailabilityHint,
  useAgentNameAvailability,
  useAgentNumberAvailability,
  useAgentEmailAvailability,
  useUsernameAvailability,
} from '@/hooks/useAvailability';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  FaArrowLeft,
  FaCog,
  FaEnvelope,
  FaFileAlt,
  FaGlobe,
  FaInfoCircle,
  FaLightbulb,
  FaLock,
  FaMobileAlt,
  FaPhoneAlt,
  FaPlusCircle,
  FaPuzzlePiece,
  FaRegUser,
  FaShieldAlt,
  FaUser,
} from 'react-icons/fa';
import { postApiCall } from '@/service/postApiCall';
import { getApiCall } from '@/service/getApiCall';
import {
  AGENT_GROUP_CREATE,
  API_AGENT_UPDATE,
  API_AGENTS_LIST,
  API_AGENT_GROUP_EXTENSION,
  API_USER_COUNTRY_CODE_DIRECTIONS_ME,
  API_USER_COUNTRY_CODE_DIRECTIONS_BY_AGENT,
  API_CALL_CENTRE_PROFILE,
  API_SIGNUP_EMAIL_SEND_CODE,
  API_SIGNUP_EMAIL_VERIFY_CODE,
  API_DEMO_ACCOUNT_SETTINGS_LIST,
  API_EXTENSION_NEXT_AVAILABLE,
  API_AGENT_CREATE_LICENCE,
} from '@/service/url';
import CountryCodeDirectionPicker from '@/components/partials/dashboard/country-code-direction/CountryCodeDirectionPicker';
import { validateEmail, sortExtensionsAscending, getAgentNameFieldError, getDefaultExtensionFieldError } from '@/utilities/helpers';
import { MODULE_ACL, FLASH_MESSAGE_ACTION_OBJECT, USER_ROLES } from '@/utilities/constant';
import { fetchAndSetFlashMessage } from '@/service/fetchFlashMessage';
import { Storage } from '@/utilities/setting';
import { generateRandomPassword } from '@/utilities/userCreation';
import {
  canEditAgentOption,
  canUseAutoAnswer,
  canUseTransfer,
  canUseConference,
  getAllowedAgentTypeSelectOptions,
  getUserModuleOperations,
} from '@/utilities/agentAcl';
import { creatorRequiresEmailVerificationOnUserForms } from '@/utilities/userEmailVerificationAcl';
import AgentAccountSettingsSection from '@/components/partials/dashboard/manage-agents/AgentAccountSettingsSection';
import { appendAgentAccountFields } from '@/utilities/agentAccountSettingsHelpers';
import {
  canManageChildVoicemail,
  getAssignedCallCentreId,
  readVoicemailEnabled,
} from '@/utilities/callCentreVoicemailAcl';
import {
  canManageChildTranscription,
  readTranscriptionEnabled,
} from '@/utilities/callCentreTranscriptionAcl';
import {
  formatAgentUniqueConflictMessage,
  getIndiaCallingCodeFromCountry,
  getIndiaCallingCodeFromDirections,
  getIndiaDirectionIds,
} from '@/utilities/agentCountryCode';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import Loader from '@/components/partials/components/Loader';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Stepper, useStepper } from '@/components/ui/stepper';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const EXTENSION_LOCAL_TYPING = /^(?:[1-4][0-9]{0,3})?$/;
const EXTENSION_LOCAL_VALID = /^[1-4][0-9]{0,3}$/;
const EXTENSION_LOCAL_HINT =
  'First digit must be 1–4 (not 0); remaining digits may be 0–9. Max 4 digits.';

export default function CreateAgent() {
  const navigate = useNavigate();
  const location = useLocation();
  const agentData: any = location.state;
  const isEditMode = Boolean(agentData);
  const [createLicenceReady, setCreateLicenceReady] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const licence: any = await getApiCall(API_AGENT_CREATE_LICENCE);
        if (cancelled) return;
        if (!licence?.allowed) {
          toast.error(licence?.message || 'You do not have a licence to create an agent.');
          navigate(sessionStorage.getItem('agentsReturnTo') || '/agents', { replace: true });
          return;
        }
        setCreateLicenceReady(true);
      } catch {
        if (!cancelled) {
          navigate(sessionStorage.getItem('agentsReturnTo') || '/agents', { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, navigate]);

  const [agentName, setAgentName] = useState(agentData?.agent_name || '');
  const [agentNumber, setAgentNumber] = useState(agentData?.agent_number || '');
  const [selectedExtension, setSelectedExtension] = useState<any>(
    agentData?.extension?.id || agentData?.extension || null,
  );
  const [createDefaultExtension, setCreateDefaultExtension] = useState(false);
  const [extensionLocal, setExtensionLocal] = useState('');
  const [suggestedExtensionLocal, setSuggestedExtensionLocal] = useState('');
  const [extensionCompanyCode, setExtensionCompanyCode] = useState('');
  const [extensionPrefix, setExtensionPrefix] = useState('');
  const [extensionLocalError, setExtensionLocalError] = useState('');
  const [nextExtensionLoading, setNextExtensionLoading] = useState(false);
  const [emailId, setEmailId] = useState(agentData?.email_id || '');
  const [description, setDescription] = useState(agentData?.description || '');
  const [username, setUsername] = useState(agentData?.username || '');
  const [password, setPassword] = useState(() => (isEditMode ? '' : generateRandomPassword()));
  const [access, setAccess] = useState(agentData?.access || 'regular');
  const [type, setType] = useState<any[]>(() =>
    Array.isArray(agentData?.type) ? agentData.type : agentData?.type ? [agentData.type] : [],
  );

  const [loginStatus, setLoginStatus] = useState(agentData?.login_status || 'Inactive');

  const [extensionOptions, setExtensionOptions] = useState<any[]>([]);
  const [, setShowToast] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [signupEmailOtp, setSignupEmailOtp] = useState('');
  const [signupEmailToken, setSignupEmailToken] = useState('');
  const [emailVerifiedForSubmit, setEmailVerifiedForSubmit] = useState(false);
  const [signupEmailBusy, setSignupEmailBusy] = useState(false);
  const [agentNameError, setAgentNameError] = useState('');


  const agentNameStatus = useAgentNameAvailability(agentName, {
    enabled: !isEditMode && agentName.trim().length > 0,
    excludeId: agentData?.id ?? null,
  });
  const agentNumberStatus = useAgentNumberAvailability(String(agentNumber ?? ''), {
    enabled: String(agentNumber ?? '').trim().length > 0,
    originalValue: isEditMode ? String(agentData?.agent_number ?? '') : null,
    excludeId: agentData?.id ?? null,
  });
  const agentEmailStatus = useAgentEmailAvailability(emailId, {
    enabled: emailId.trim().length > 0 && !validateEmail(emailId),
    originalValue: isEditMode ? String(agentData?.email_id ?? '') : null,
    excludeId: agentData?.id ?? null,
  });
  const [usernameError, setUsernameError] = useState('');
  const agentUsernameStatus = useUsernameAvailability(username, {
    enabled: !isEditMode && username.trim().length > 0 && /^[a-zA-Z0-9_]+$/.test(username.trim()),
  });
  const existingAgentNamesRef = useRef(new Set());
  const existingExtensionsRef = useRef([]);
  const [existingNamesTick, setExistingNamesTick] = useState(0);
  const [existingExtensionsTick, setExistingExtensionsTick] = useState(0);
  const [notes, setNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);
  const [showPassword, setShowPassword] = useState(!isEditMode);
  const [showExtensionConfirm, setShowExtensionConfirm] = useState(false);
  const [pendingExtension, setPendingExtension] = useState<any>(null);

  const [countryCode, setCountryCode] = useState(agentData?.country_code || '');
  const [countryCodeError, setCountryCodeError] = useState('');
  // const [cliOption, setCliOption] = useState(agentData?.cli_option || 'actual'); // cli_option removed: always 'actual'
  const [isOutboundEnabled, setIsOutboundEnabled] = useState(
    typeof agentData?.is_outbound_enabled === 'boolean' ? agentData.is_outbound_enabled : false,
  );
  // agents.auto_pick_enabled — incoming calls are answered automatically.
  const [autoAnswer, setAutoAnswer] = useState(agentData?.auto_pick_enabled === true);
  // agents.show_auto_answer_toggle — the agent gets an Auto Answer switch on their dialer
  // (it changes auto_pick_enabled). Off: only the admin sets auto_pick_enabled.
  const [showAutoAnswerToggle, setShowAutoAnswerToggle] = useState(agentData?.show_auto_answer_toggle === true);
  // agents.is_transfer_enabled / is_conference_enabled — Transfer / Conference buttons on the dialer.
  const [transferEnabled, setTransferEnabled] = useState(agentData?.is_transfer_enabled === true);
  const [conferenceEnabled, setConferenceEnabled] = useState(agentData?.is_conference_enabled === true);
  // agents.allow_early_follow_up — follow-up Call button works before the scheduled time.
  const [earlyFollowUp, setEarlyFollowUp] = useState(agentData?.allow_early_follow_up === true);
  const [firstCallAcceptance, setFirstCallAcceptance] = useState(
    agentData && Object.prototype.hasOwnProperty.call(agentData, 'first_call_acceptance')
      ? agentData.first_call_acceptance || 'agent'
      : 'agent',
  );
  const [voicemailEnabled, setVoicemailEnabled] = useState(() => readVoicemailEnabled(agentData));
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(() => readTranscriptionEnabled(agentData));
  const [maskingEnabled, setMaskingEnabled] = useState(
    typeof agentData?.is_masking_enabled === 'boolean' ? agentData.is_masking_enabled : false,
  );
  const [callCentreProfile, setCallCentreProfile] = useState<any>(null);

  const [passwordRules, setPasswordRules] = useState({
    length: !isEditMode,
    upper: !isEditMode,
    lower: !isEditMode,
    number: !isEditMode,
    special: !isEditMode,
    space: true,
  });
  const [passwordError, setPasswordError] = useState<any>('');

  const [availableCcDirections, setAvailableCcDirections] = useState<any[]>([]);
  const [selectedCcDirections, setSelectedCcDirections] = useState<any[]>([]);

  const [selectedACL, setSelectedACL] = useState('');
  const [accountType, setAccountType] = useState('0');
  const [expiryDate, setExpiryDate] = useState('');
  const [parentAccountType, setParentAccountType] = useState('0');
  const [parentExpiryDate, setParentExpiryDate] = useState<any>(null);

  const accessOptions = [
    { label: 'Select Access', value: '' },
    { label: 'Team Lead', value: 'team_lead' },
    { label: 'Regular', value: 'regular' },
  ];

  const firstCallAcceptanceOptions = [
    { label: 'Agent', value: 'agent' },
    { label: 'Customer', value: 'customer' },
  ];

  const agentAclContext = useMemo(() => {
    try {
      const raw = Storage.getItem('user');
      const user = raw ? JSON.parse(raw) : null;
      return { userRole: user?.role, isSuperAdmin: user?.role === USER_ROLES.SUPERADMIN };
    } catch {
      return { userRole: null, isSuperAdmin: false };
    }
  }, []);

  const userModuleOps = useMemo(() => {
    try {
      const raw = Storage.getItem('user');
      const user = raw ? JSON.parse(raw) : null;
      return getUserModuleOperations(user);
    } catch {
      return {};
    }
  }, []);

  const typeOptions = useMemo(
    () => getAllowedAgentTypeSelectOptions(userModuleOps, agentAclContext),
    [userModuleOps, agentAclContext],
  );

  const isDemoCompanyAgent = useMemo(() => {
    try {
      const user = JSON.parse(Storage.getItem('user') || 'null');
      const company = user?.assignedCompany || user?.assigned_company_details || user?.company || null;
      return company?.is_demo_company === true;
    } catch {
      return false;
    }
  }, []);

  const canOutboundEnabled = useMemo(
    () => canEditAgentOption(userModuleOps, 'outbound_enabled', agentAclContext),
    [userModuleOps, agentAclContext],
  );
  const canFirstCallConnect = useMemo(
    () => canEditAgentOption(userModuleOps, 'first_call_connect', agentAclContext),
    [userModuleOps, agentAclContext],
  );
  const canAutoAnswer = useMemo(
    () => canUseAutoAnswer(userModuleOps, agentAclContext),
    [userModuleOps, agentAclContext],
  );
  // Transfer / Conference toggles show only when the ACL grants them (any agent type).
  const canTransfer = useMemo(
    () => canUseTransfer(userModuleOps, agentAclContext),
    [userModuleOps, agentAclContext],
  );
  const canConference = useMemo(
    () => canUseConference(userModuleOps, agentAclContext),
    [userModuleOps, agentAclContext],
  );
  // Early follow-up call toggle: Disposition Forms ACL "allow_followups" + "early_follow_up".
  const canEarlyFollowUp = useMemo(
    () => canUseEarlyFollowUp(userModuleOps, agentAclContext),
    [userModuleOps, agentAclContext],
  );
  // Auto answer applies to WEBRTC agents only; hidden (and sent as false) for other types.
  const showAutoAnswer = canAutoAnswer && Array.isArray(type) && type.includes('WEBRTC');
  const canVoicemailEnabled = useMemo(
    () => canManageChildVoicemail(callCentreProfile),
    [callCentreProfile],
  );
  const canTranscriptionEnabled = useMemo(
    () => canManageChildTranscription(callCentreProfile),
    [callCentreProfile],
  );

  const isCallCentreAdmin = agentAclContext.userRole === USER_ROLES.CALL_CENTRE_ADMIN;

  const emailVerificationRequired = useMemo(() => {
    try {
      const raw = Storage.getItem('user');
      const user = raw ? JSON.parse(raw) : null;
      return creatorRequiresEmailVerificationOnUserForms(user, {
        userRole: user?.role,
        isSuperAdmin: user?.role === USER_ROLES.SUPERADMIN,
      });
    } catch {
      return false;
    }
  }, []);

  const originalAgentEmail = useMemo(
    () => String(agentData?.email_id || '').trim().toLowerCase(),
    [agentData?.email_id],
  );
  const emailChangedForEdit = useMemo(
    () => emailId.trim().toLowerCase() !== originalAgentEmail,
    [emailId, originalAgentEmail],
  );
  const needsEmailVerificationForSubmit = useMemo(() => {
    if (!emailVerificationRequired) return false;
    if (!isEditMode) return true;
    return emailChangedForEdit;
  }, [emailVerificationRequired, isEditMode, emailChangedForEdit]);

  useEffect(() => {
    if (!isCallCentreAdmin) return;
    try {
      const raw = Storage.getItem('user');
      const user = raw ? JSON.parse(raw) : null;
      if (user?.account_type !== undefined && user?.account_type !== null) {
        setParentAccountType(String(user.account_type));
        setAccountType(String(user.account_type));
      }
      if (user?.expiry_date) setParentExpiryDate(user.expiry_date);
      if (!isDemoCompanyAgent) {
        setExpiryDate((prev) => {
          if (prev) return prev;
          const defaultExpiry = new Date();
          defaultExpiry.setMonth(defaultExpiry.getMonth() + 1);
          return defaultExpiry.toISOString().split('T')[0];
        });
      }
    } catch {
      /* ignore */
    }
  }, [isCallCentreAdmin, isDemoCompanyAgent]);

  useEffect(() => {
    if (!isCallCentreAdmin || !isDemoCompanyAgent || isEditMode) return;
    getApiCall(API_DEMO_ACCOUNT_SETTINGS_LIST, {})
      .then((resp: any) => {
        const row = Array.isArray(resp) ? resp[0] : resp;
        const days = Number(row?.expiry_days_per_recharge) || 0;
        if (days <= 0) return;
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + (days - 1));
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        setExpiryDate(`${y}-${m}-${day}`);
      })
      .catch(() => {});
  }, [isCallCentreAdmin, isDemoCompanyAgent, isEditMode]);

  useEffect(() => {
    if (!isEditMode && !createLicenceReady) return;
    getApiCall(`${API_AGENT_GROUP_EXTENSION}?data=all`)
      .then((data: any) => {
        const list = sortExtensionsAscending(Array.isArray(data) ? data : []);
        existingExtensionsRef.current = list;
        setExistingExtensionsTick((t) => t + 1);
        const available = list.filter((ext) => ext.is_allocated == 0);
        const options = available.map((extension) => ({
          label: extension.username,
          value: extension.id,
        }));
        const updated = [{ label: 'Select Extension', value: null }, ...options];
        setExtensionOptions(updated);
      })
      .catch(() => {
        toast.error('Error fetching extensions.');
        setShowToast(true);
      });

    getApiCall(API_AGENTS_LIST, { fields: 'min' })
      .then((resp: any) => {
        const list = Array.isArray(resp) ? resp : [];
        const names = new Set();
        const excludeId = isEditMode && agentData?.id ? String(agentData.id) : null;
        list.forEach((a) => {
          if (excludeId && String(a.id) === excludeId) return;
          const n = String(a?.agent_name || '').trim().toLowerCase();
          if (n) names.add(n);
        });
        existingAgentNamesRef.current = names;
        setExistingNamesTick((t) => t + 1);
      })
      .catch(() => {});

    fetchAndSetFlashMessage(
      (msg) =>
        msg.module_name === MODULE_ACL.AGENTS &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD &&
        msg.type === 'Note',
      setNotes,
    );

    getApiCall(API_USER_COUNTRY_CODE_DIRECTIONS_ME, {})
      .then((data: any) => {
        const list = (Array.isArray(data) ? data : []).map((row) => row.directionMaster).filter(Boolean);
        setAvailableCcDirections(list);
      })
      .catch(() => setAvailableCcDirections([]));

    try {
      const raw = Storage.getItem('user');
      const user = raw ? JSON.parse(raw) : null;
      const ccId = getAssignedCallCentreId(user);
      if (ccId) {
        getApiCall(API_CALL_CENTRE_PROFILE.replace(':id', ccId))
          .then((resp) => setCallCentreProfile(resp))
          .catch(() => setCallCentreProfile(null));
      } else {
        setCallCentreProfile(null);
      }
    } catch {
      setCallCentreProfile(null);
    }

    if (isEditMode && agentData?.id) {
      getApiCall(API_USER_COUNTRY_CODE_DIRECTIONS_BY_AGENT(agentData.id), {})
        .then((data: any) => {
          const ids = (Array.isArray(data) ? data : [])
            .map((row) => row.country_code_direction_id)
            .filter(Boolean);
          setSelectedCcDirections(ids);
        })
        .catch(() => {});
    }
  }, [isEditMode, createLicenceReady]);

  useEffect(() => {
    if (isEditMode) return;
    if (selectedCcDirections.length > 0) return;
    const fromMe = getIndiaDirectionIds(availableCcDirections);
    const fromCc = getIndiaDirectionIds(
      (callCentreProfile?.callcenterCountryCodeDirections || []).map(
        (row: any) => row.directionMaster,
      ),
    );
    const ids = fromMe.length ? fromMe : fromCc;
    if (ids.length) setSelectedCcDirections(ids);
  }, [availableCcDirections, callCentreProfile, isEditMode, selectedCcDirections.length]);

  useEffect(() => {
    if (isEditMode) return;
    if (countryCode) return;
    const fromDirections = getIndiaCallingCodeFromDirections(availableCcDirections);
    const fromCcProfile = getIndiaCallingCodeFromCountry(callCentreProfile?.country_code);
    const fromCcDirections = getIndiaCallingCodeFromDirections(
      (callCentreProfile?.callcenterCountryCodeDirections || []).map(
        (row: any) => row.directionMaster,
      ),
    );
    const next = fromDirections || fromCcProfile || fromCcDirections;
    if (next) setCountryCode(next);
  }, [availableCcDirections, callCentreProfile, isEditMode, countryCode]);

  useEffect(() => {
    if (isEditMode || !createDefaultExtension) return;

    let cancelled = false;
    setNextExtensionLoading(true);
    getApiCall(API_EXTENSION_NEXT_AVAILABLE)
      .then((data: any) => {
        if (cancelled || !data) return;
        setExtensionCompanyCode(data.company_code || '');
        setExtensionPrefix(data.prefix || '');
        setSuggestedExtensionLocal(data.local || '');
        setExtensionLocal(data.local || '');
      })
      .catch(() => {
        if (!cancelled) {
          setSuggestedExtensionLocal('');
          setExtensionLocal('');
        }
      })
      .finally(() => {
        if (!cancelled) setNextExtensionLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isEditMode, createDefaultExtension]);

  useEffect(() => {
    if (!createDefaultExtension) return;
    const namespace = `${extensionCompanyCode || ''}${extensionPrefix || ''}`;
    setExtensionLocalError(
      getDefaultExtensionFieldError(
        extensionLocal,
        existingExtensionsRef.current,
        namespace,
      ),
    );
  }, [
    createDefaultExtension,
    extensionLocal,
    extensionCompanyCode,
    extensionPrefix,
    existingExtensionsTick,
  ]);

  useEffect(() => {
    if (!agentName) return;
    const msg = getAgentNameFieldError(agentName, existingAgentNamesRef.current);
    setAgentNameError(msg);
  }, [existingNamesTick]);

  const validatePassword = (value) => {
    const rules = {
      length: value.length >= 8,
      upper: /[A-Z]/.test(value),
      lower: /[a-z]/.test(value),
      number: /[0-9]/.test(value),
      special: /[!@#$%^&*(),.?":{}|<>_\-\\[\]\/+=~`]/.test(value),
      space: !/\s/.test(value),
    };
    setPasswordRules(rules);
    if (value && !Object.values(rules).every(Boolean)) {
      setPasswordError('Password does not meet requirements');
    } else {
      setPasswordError('');
    }
  };

  const handleSaveAgent = async () => {
    if (loading || submittingRef.current) return;
    if (agentNameStatus === 'taken') return void toast.error('Agent name already exists');
    if (agentNumberStatus === 'taken') return void toast.error('Agent number already exists');
    if (emailError) return void toast.error('Enter correct Email.');
    if (agentEmailStatus === 'taken') return void toast.error('Email already exists');
    if (!isEditMode && agentUsernameStatus === 'taken') return void toast.error('Username already exists');
    const nameErr = getAgentNameFieldError(agentName, existingAgentNamesRef.current);
    if (nameErr) {
      setAgentNameError(nameErr);
      return void toast.error(nameErr);
    }
    if (!validateUsername(username)) return void toast.error(usernameError || 'Username validation failed');
    if (!type || type?.length < 1) return void toast.error('Type required');
    if (!typeOptions.length) {
      return void toast.error('Your ACL does not allow any agent types (Mobile / Soft Phone / WEBRTC).');
    }

    if (!isEditMode && !Object.values(passwordRules).every(Boolean)) {
      return void toast.error('Password must meet all requirements');
    }

    if (Array.isArray(type) && !(type.includes('Soft Phone') || type.includes('WEBRTC')) && (selectedExtension || createDefaultExtension)) {
      return void toast.error('Extension will be allocated only in case of Type Soft Phone or Type Webrtc.');
    }
    if (!isEditMode && createDefaultExtension) {
      if (extensionLocalError) {
        return void toast.error(extensionLocalError);
      }
      if (extensionLocal && !EXTENSION_LOCAL_VALID.test(extensionLocal)) {
        return void toast.error(EXTENSION_LOCAL_HINT);
      }
    }
    if (type.includes('Mobile')) {
      if (!validateCountryCode(countryCode)) {
        return void toast.error(countryCodeError || 'Invalid country code');
      }
    }
    // cli_option removed: always 'actual', nothing to pick.
    // if (type.includes('WEBRTC')) {
    //   if (!cliOption) return void toast.error('Select incoming call display option');
    // }
    if (isCallCentreAdmin) {
      if (!expiryDate && !isDemoCompanyAgent) return void toast.error('Account expiry date is required');
    }

    if (needsEmailVerificationForSubmit && (!emailVerifiedForSubmit || !signupEmailToken)) {
      return void toast.error('Verify this email first: Send code, enter OTP, then Verify OTP.');
    }

    const payload: any = {
      agent_name: agentName,
      agent_number: Number(agentNumber),
      extension: !isEditMode && createDefaultExtension ? [] : selectedExtension ? [selectedExtension] : [],
      email_id: emailId,
      description,
      access,
      type,
      login_status: loginStatus,
      country_code: type.includes('Mobile') ? countryCode : null,
      // cli_option: type.includes('WEBRTC') ? cliOption : null,
      cli_option: type.includes('WEBRTC') ? 'actual' : null,
      is_outbound_enabled: !!isOutboundEnabled,
      is_masking_enabled: !!maskingEnabled,
      first_call_acceptance: type.includes('Mobile') ? firstCallAcceptance || 'agent' : 'agent',
      country_code_directions: Array.isArray(selectedCcDirections) ? selectedCcDirections.map(Number) : [],
    };

    if (!isEditMode) {
      payload.username = username.trim();
      payload.password = password;
      if (createDefaultExtension) {
        payload.create_default_extension = true;
        if (extensionLocal && extensionLocal !== suggestedExtensionLocal) {
          payload.extension_local = extensionLocal;
        }
      }
    }
    if (canOutboundEnabled) payload.is_outbound_enabled = !!isOutboundEnabled;
    if (canAutoAnswer) {
      payload.auto_pick_enabled = showAutoAnswer ? !!autoAnswer : false;
      payload.show_auto_answer_toggle = showAutoAnswer ? !!showAutoAnswerToggle : false;
    }
    if (canTransfer) payload.is_transfer_enabled = !!transferEnabled;
    if (canConference) payload.is_conference_enabled = !!conferenceEnabled;
    if (canEarlyFollowUp) payload.allow_early_follow_up = !!earlyFollowUp;
    if (canFirstCallConnect) {
      payload.first_call_acceptance = type.includes('Mobile') ? firstCallAcceptance || 'agent' : 'agent';
    }
    if (canVoicemailEnabled) payload.is_voicemail_enabled = !!voicemailEnabled;
    if (canTranscriptionEnabled) payload.is_transcription_enabled = !!transcriptionEnabled;

    submittingRef.current = true;
    setLoading(true);

    if (isCallCentreAdmin) {
      appendAgentAccountFields(payload, { selectedACL, accountType, expiryDate });
    }

    if (needsEmailVerificationForSubmit && signupEmailToken) {
      payload.signup_email_token = signupEmailToken;
    }

    try {
      setLoading(true);
      if (isEditMode) {
        await postApiCall(`${API_AGENT_UPDATE}/${agentData.id}`, payload);
        toast.success('Agent updated successfully');
        setTimeout(() => navigate(sessionStorage.getItem('agentsReturnTo') || '/agents'), 2000);
      } else {
        await postApiCall(AGENT_GROUP_CREATE, payload);
        toast.success('Agent created successfully');
        setTimeout(() => navigate(sessionStorage.getItem('agentsReturnTo') || '/agents'), 3000);
      }
    } catch (err: any) {
      const msg =
        typeof err === 'string' ? err : err?.response?.data?.message || err?.message || String(err);
      toast.error(formatAgentUniqueConflictMessage(friendlyApiError(err) || msg));
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmailId(value);
    setEmailError(validateEmail(value));
    setSignupEmailToken('');
    setSignupEmailOtp('');
    setEmailVerifiedForSubmit(false);
  };

  async function sendSignupEmailCode() {
    if (!emailId.trim() || validateEmail(emailId)) {
      toast.error('Enter a valid email before sending a code.');
      return;
    }
    setSignupEmailBusy(true);
    try {
      await postApiCall(API_SIGNUP_EMAIL_SEND_CODE, { email: emailId.trim() });
      setSignupEmailToken('');
      setEmailVerifiedForSubmit(false);
      toast.success('Verification code sent. Check the inbox for this email.');
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Could not send verification code');
    } finally {
      setSignupEmailBusy(false);
    }
  }

  async function verifySignupEmailOtp() {
    if (!emailId.trim() || validateEmail(emailId)) {
      toast.error('Enter a valid email.');
      return;
    }
    if (!signupEmailOtp || signupEmailOtp.length !== 6) {
      toast.error('Enter the 6-digit code from the email.');
      return;
    }
    setSignupEmailBusy(true);
    try {
      const data: any = await postApiCall(API_SIGNUP_EMAIL_VERIFY_CODE, {
        email: emailId.trim(),
        otp: signupEmailOtp.trim(),
      });
      if (data?.signupEmailToken) {
        setSignupEmailToken(data.signupEmailToken);
        setEmailVerifiedForSubmit(true);
        toast.success('Email verified. You can save the agent now.');
      } else {
        toast.error('Unexpected response from server');
      }
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Verification failed');
      setEmailVerifiedForSubmit(false);
      setSignupEmailToken('');
    } finally {
      setSignupEmailBusy(false);
    }
  }

  const validateAgentName = (value) => {
    const msg = getAgentNameFieldError(value, existingAgentNamesRef.current);
    setAgentNameError(msg);
    return !msg;
  };

  const validateUsername = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      setUsernameError('Username is required');
      return false;
    }
    const usernameRegex = /^[a-zA-Z0-9_]+$/;
    if (!usernameRegex.test(trimmedValue)) {
      setUsernameError(
        'Username can only contain letters, numbers, and underscores (_). No spaces or special characters allowed.',
      );
      return false;
    }
    setUsernameError('');
    return true;
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    validatePassword(value);

    const errors: string[] = [];
    if (value.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(value)) errors.push('At least 1 uppercase letter');
    if (!/[a-z]/.test(value)) errors.push('At least 1 lowercase letter');
    if (!/[0-9]/.test(value)) errors.push('At least 1 number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) errors.push('At least 1 special character');
    if (/\s/.test(value)) errors.push('No spaces allowed');

    if (value.length === 0) setPasswordError('');
    else if (errors.length > 0) setPasswordError(errors);
    else setPasswordError('');
  };

  const validateCountryCode = (value) => {
    if (!value) {
      setCountryCodeError('Country code is required');
      return false;
    }
    if (!/^\d{1,4}$/.test(value)) {
      setCountryCodeError('Only numbers allowed (1–4 digits)');
      return false;
    }
    setCountryCodeError('');
    return true;
  };

  const requiredMark = <span className="ml-1 text-destructive">*</span>;
  const cardStyle: any = {
    border: '1px solid hsl(var(--border) / 0.6)',
    borderRadius: 12,
    background: 'hsl(var(--card))',
    color: 'hsl(var(--foreground))',
    boxShadow:
      '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
  };
  const labelStyle: any = {
    marginBottom: 8,
    color: 'hsl(var(--foreground))',
    fontWeight: 500,
  };
  const inputStyle: any = {
    minHeight: 40,
    border: '1px solid hsl(var(--border) / 0.6)',
    borderRadius: 6,
    fontSize: 13,
    background: 'hsl(var(--card))',
    color: 'hsl(var(--foreground))',
    boxShadow:
      'inset 2px 2px 5px rgba(163, 177, 198, 0.35), inset -2px -2px 5px rgba(255, 255, 255, 0.9)',
  };
  const iconInputStyle: any = { ...inputStyle, paddingLeft: 39 };
  const inputWrapperStyle: any = { position: 'relative' };
  const inputIconStyle: any = {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'hsl(var(--primary))',
    fontSize: 13,
    zIndex: 2,
    pointerEvents: 'none',
  };
  const noteText =
    notes.find((n) => Number(n.priority) === 1)?.message ||
    'Extension will be allocated only in case of Type Soft Phone or Type Webrtc.';

  const hasSipExtension =
    selectedExtension !== null || (!isEditMode && createDefaultExtension);
  const typeSelectOptions = useMemo(
    () =>
      hasSipExtension
        ? typeOptions
        : typeOptions.filter((o) => o.value === 'Mobile'),
    [hasSipExtension, typeOptions],
  );

  // Drop types the user is no longer allowed to assign (e.g. Soft Phone without extension).
  useEffect(() => {
    const allowed = new Set(typeSelectOptions.map((o) => o.value));
    setType((prev) => {
      const next = prev.filter((t) => allowed.has(t));
      return next.length === prev.length ? prev : next;
    });
  }, [typeSelectOptions]);

  const stepDescriptors = [
    {
      id: 'basic',
      label: 'Basic Information',
      description: 'Identity & credentials',
      icon: FaRegUser as any,
    },
    {
      id: 'settings',
      label: 'Settings & Description',
      description: 'Preferences and notes',
      icon: FaCog as any,
    },
    ...(isCallCentreAdmin
      ? [
          {
            id: 'account',
            label: 'Account Settings',
            description: 'ACL and expiry',
            icon: FaShieldAlt as any,
          },
        ]
      : []),
  ];
  const stepper = useStepper(stepDescriptors.length, 0);

  if (!createLicenceReady) {
    return (
      <ScrollPage>
        <Loader />
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <div className="mx-auto max-w-6xl px-1 py-1">
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="mb-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border/60 bg-card text-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.35),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
              aria-label="Go back"
              title="Go back"
            >
              <FaArrowLeft size={13} />
            </button>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <FaUser size={18} />
              </span>
              <div>
                <h4 className="m-0 text-lg font-bold text-foreground">
                  {isEditMode ? 'Edit Agent' : 'Create Agent'}
                </h4>
                <p className="m-0 text-xs text-muted-foreground">
                  {isEditMode
                    ? 'Update agent details in your system.'
                    : 'Add a new agent to your system.'}
                </p>
              </div>
            </div>
          </div>

          <div
            className="mb-3 flex items-start gap-2 rounded-lg border border-primary/25 bg-primary/[0.06] px-3 py-2 text-xs text-foreground"
            role="alert"
          >
            <FaInfoCircle size={15} className="mt-0.5 shrink-0 text-primary" />
            <span>
              <strong>Note:</strong> {noteText}
            </span>
          </div>

          <div
            className="mb-4 rounded-xl border border-border/60 bg-card px-5 py-4"
            style={{
              boxShadow:
                '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
            }}
          >
            <Stepper
              steps={stepDescriptors}
              current={stepper.current}
              completed={stepper.completed}
              onStepClick={stepper.goTo}
            />
          </div>

          {stepper.current === 0 && (
          <Card style={cardStyle} className="mb-3">
            <CardContent style={{ padding: 20 }}>
              <div className="d-flex align-items-start gap-2 mb-4">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <FaRegUser size={14} />
                </span>
                <div>
                  <p className="m-0 text-sm font-bold text-foreground">Basic Information</p>
                  <p className="m-0 text-xs text-muted-foreground">Enter the primary details of the agent.</p>
                </div>
              </div>

              <div className="row g-4">
                <div className="col-md-4">
                  <label htmlFor="agentName" className="mb-1.5 block text-xs font-semibold text-foreground" style={labelStyle}>
                    Agent Name {requiredMark}
                  </label>
                  <div style={inputWrapperStyle}>
                    <FaRegUser style={inputIconStyle} />
                    <input
                      type="text"
                      id="agentName"
                      value={agentName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setAgentName(value);
                        validateAgentName(value);
                      }}
                      onBlur={(e) => validateAgentName(e.target.value)}
                      className={`form-control ${agentNameError ? 'is-invalid' : ''}`}
                      placeholder="Enter agent name"
                      required
                      disabled={isEditMode}
                      style={iconInputStyle}
                    />
                  </div>
                  {agentNameError && <div className="invalid-feedback d-block">{agentNameError}</div>}
                  {!agentNameError && (
                    <AvailabilityHint status={agentNameStatus} takenMessage="Agent name already exists" />
                  )}
                </div>

                <div className="col-md-4">
                  <label htmlFor="agentNumber" className="mb-1.5 block text-xs font-semibold text-foreground" style={labelStyle}>
                    Agent Number {requiredMark}
                  </label>
                  <div style={inputWrapperStyle}>
                    <FaPhoneAlt style={inputIconStyle} />
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={10}
                      id="agentNumber"
                      value={agentNumber}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d{0,10}$/.test(val)) setAgentNumber(val);
                      }}
                      className={`form-control ${agentNumberStatus === 'taken' ? 'is-invalid' : ''}`}
                      placeholder="Enter agent number"
                      required
                      style={iconInputStyle}
                    />
                  </div>
                  <AvailabilityHint status={agentNumberStatus} takenMessage="Agent number already exists" />
                </div>

                <div className="col-md-4">
                  <label htmlFor="emailId" className="mb-1.5 block text-xs font-semibold text-foreground" style={labelStyle}>
                    Email ID {requiredMark}
                  </label>
                  <div style={inputWrapperStyle}>
                    <FaEnvelope style={inputIconStyle} />
                    <input
                      type="email"
                      id="emailId"
                      value={emailId}
                      onChange={handleEmailChange}
                      className="form-control"
                      placeholder="Enter email address"
                      required
                      style={iconInputStyle}
                    />
                  </div>
                  {emailError && <span className="text-danger">{emailError}</span>}
                  {!emailError && <AvailabilityHint status={agentEmailStatus} takenMessage="Email already exists" />}
                  {needsEmailVerificationForSubmit && (
                    <div className="d-flex flex-wrap align-items-end gap-2 mt-2">
                      {emailVerifiedForSubmit ? (
                        <span className="text-success small">Email verified</span>
                      ) : (
                        <>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="6-digit code"
                            maxLength={6}
                            className="form-control form-control-sm"
                            style={{ maxWidth: '140px' }}
                            value={signupEmailOtp}
                            onChange={(e) =>
                              setSignupEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                            }
                            disabled={signupEmailBusy}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            disabled={signupEmailBusy || !emailId.trim() || !!validateEmail(emailId)}
                            onClick={sendSignupEmailCode}
                          >
                            Send code
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-success btn-sm"
                            disabled={signupEmailBusy || signupEmailOtp.length !== 6}
                            onClick={verifySignupEmailOtp}
                          >
                            Verify OTP
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="col-md-4">
                  <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                    <label htmlFor="extensionDropdown" className="mb-0 block text-xs font-semibold text-foreground" style={labelStyle}>
                      Extension
                    </label>
                    {!isEditMode && (
                      <label
                        htmlFor="createDefaultExtension"
                        className="mb-0 inline-flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-foreground"
                      >
                        <input
                          id="createDefaultExtension"
                          type="checkbox"
                          className="form-check-input m-0"
                          checked={createDefaultExtension}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setCreateDefaultExtension(checked);
                            if (checked) {
                              setSelectedExtension(null);
                              setPendingExtension(null);
                              setShowExtensionConfirm(false);
                            } else {
                              setExtensionLocal('');
                              setSuggestedExtensionLocal('');
                              setExtensionCompanyCode('');
                              setExtensionPrefix('');
                              setExtensionLocalError('');
                              setNextExtensionLoading(false);
                              setType((prev) =>
                                prev.filter((t) => t !== 'Soft Phone' && t !== 'WEBRTC'),
                              );
                            }
                          }}
                        />
                        Create default extension
                      </label>
                    )}
                  </div>
                  <div style={inputWrapperStyle}>
                    <FaPuzzlePiece style={inputIconStyle} />
                    <select
                      id="extensionDropdown"
                      value={selectedExtension || ''}
                      disabled={!isEditMode && createDefaultExtension}
                      onChange={(e) => {
                        const value = e.target.value || null;
                        if (value) {
                          setPendingExtension(value);
                          setShowExtensionConfirm(true);
                          return;
                        }
                        setSelectedExtension(value);
                        setType((prev) => prev.filter((t) => t !== 'Soft Phone' && t !== 'WEBRTC'));
                      }}
                      className="form-control form-select"
                      style={
                        !isEditMode && createDefaultExtension
                          ? { ...iconInputStyle, opacity: 0.65, cursor: 'not-allowed' }
                          : iconInputStyle
                      }
                    >
                      {extensionOptions.map((ext) => (
                        <option key={ext.value} value={ext.value ?? ''}>
                          {ext.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!isEditMode && createDefaultExtension && (
                    <div className="mt-2">
                      <div className="flex overflow-hidden rounded-md ring-1 ring-inset ring-border/60">
                        <span className="inline-flex items-center bg-muted px-2.5 font-mono text-xs font-semibold text-muted-foreground">
                          {extensionCompanyCode}
                          {extensionPrefix}
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={4}
                          value={extensionLocal}
                          disabled={nextExtensionLoading}
                          placeholder={nextExtensionLoading ? '…' : 'e.g. 1000'}
                          onChange={(e) => {
                            const input = e.target.value;
                            if (EXTENSION_LOCAL_TYPING.test(input)) {
                              setExtensionLocal(input);
                            }
                          }}
                          className={`form-control ${extensionLocalError ? 'is-invalid' : ''}`}
                          style={{
                            border: 'none',
                            boxShadow: 'none',
                            borderRadius: 0,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            letterSpacing: '0.06em',
                            fontSize: 13,
                            height: 41,
                          }}
                        />
                      </div>
                      {extensionLocalError ? (
                        <div className="invalid-feedback d-block">{extensionLocalError}</div>
                      ) : (
                        <p className="mb-0 mt-1 text-[11px] text-muted-foreground">
                          {nextExtensionLoading
                            ? 'Finding the next free extension…'
                            : 'Auto-generated. You can edit the last 1–4 digits. Extension password will be this number.'}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="col-md-4">
                  <label htmlFor="username" className="mb-1.5 block text-xs font-semibold text-foreground" style={labelStyle}>
                    Username {requiredMark}
                  </label>
                  <div style={isEditMode ? { ...inputWrapperStyle, pointerEvents: 'none' } : inputWrapperStyle}>
                    <FaUser style={isEditMode ? { ...inputIconStyle, opacity: 0.55 } : inputIconStyle} />
                    <input
                      type="text"
                      id="username"
                      autoComplete="off"
                      value={username}
                      readOnly={isEditMode}
                      disabled={isEditMode}
                      tabIndex={isEditMode ? -1 : undefined}
                      onChange={(e) => {
                        const value = e.target.value;
                        setUsername(value);
                        validateUsername(value);
                      }}
                      onBlur={(e) => validateUsername(e.target.value)}
                      className={`form-control ${usernameError ? 'is-invalid' : ''}${isEditMode ? ' agent-form-readonly' : ''}`}
                      placeholder="Enter username"
                      required
                      style={iconInputStyle}
                    />
                  </div>
                  {usernameError && <div className="invalid-feedback">{usernameError}</div>}
                  {!usernameError && (
                    <AvailabilityHint status={agentUsernameStatus} takenMessage="Username already exists" />
                  )}
                </div>

                <div className="col-md-4">
                  <label htmlFor="password" className="mb-1.5 block text-xs font-semibold text-foreground" style={labelStyle}>
                    Password {!isEditMode && requiredMark}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <FaLock style={inputIconStyle} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      autoComplete="off"
                      readOnly
                      onFocus={(e: any) => e.target.removeAttribute('readOnly')}
                      onChange={handlePasswordChange}
                      className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                      placeholder={isEditMode ? 'Enter new password' : 'Enter password'}
                      required={!isEditMode}
                      style={{ ...iconInputStyle, paddingRight: 42 }}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: 14,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        color: '#64748b',
                        fontSize: 15,
                      }}
                    >
                      {showPassword ? (
                        <i className="fa-solid fa-eye-slash" />
                      ) : (
                        <i className="fa-solid fa-eye" />
                      )}
                    </span>
                  </div>
                  {Array.isArray(passwordError) && passwordError.length > 0 && (
                    <div style={{ color: '#dc3545', fontSize: 13, marginTop: 5 }}>
                      {passwordError.map((err, index) => (
                        <div key={index}>• {err}</div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="col-md-4">
                  <label htmlFor="accessDropdown" className="mb-1.5 block text-xs font-semibold text-foreground" style={labelStyle}>
                    Access {requiredMark}
                  </label>
                  <div style={inputWrapperStyle}>
                    <FaShieldAlt style={inputIconStyle} />
                    <select
                      id="accessDropdown"
                      value={access || 'regular'}
                      onChange={(e) => setAccess(e.target.value)}
                      className="form-control form-select"
                      style={iconInputStyle}
                    >
                      {accessOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-md-4">
                  <label htmlFor="typeDropdown" className="mb-1.5 block text-xs font-semibold text-foreground" style={labelStyle}>
                    Type {requiredMark}
                  </label>
                  <div style={inputWrapperStyle}>
                    <FaMobileAlt style={inputIconStyle} />
                    <Select
                      isMulti
                      inputId="typeDropdown"
                      value={type.map((v) => ({ label: v, value: v }))}
                      options={typeSelectOptions}
                      onChange={(selected: any) => setType((selected || []).map((o) => o.value))}
                      placeholder="Select type"
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: 41,
                          border: '1px solid #dbe3ef',
                          borderRadius: 6,
                          paddingLeft: 30,
                          fontSize: 12,
                          boxShadow: 'none',
                        }),
                        menu: (base) => ({ ...base, zIndex: 5 }),
                      }}
                      menuPlacement="auto"
                      className="theme-react-select"
                      classNamePrefix="theme-react-select"
                    />
                  </div>
                </div>
              </div>

              {type.includes('Mobile') && (
                <div className="row g-4 mt-1">
                  <div className="col-md-4">
                    <label className="mb-1.5 block text-xs font-semibold text-foreground" style={labelStyle}>
                      Country Code (Mobile) {requiredMark}
                    </label>
                    <div style={inputWrapperStyle}>
                      <FaGlobe style={inputIconStyle} />
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                        value={countryCode}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d{0,4}$/.test(val)) {
                            setCountryCode(val);
                            validateCountryCode(val);
                          }
                        }}
                        className={`form-control ${countryCodeError ? 'is-invalid' : ''}`}
                        placeholder="e.g. 91"
                        style={iconInputStyle}
                      />
                    </div>
                    {countryCodeError && <div className="invalid-feedback">{countryCodeError}</div>}
                  </div>
                </div>
              )}

              {/* cli_option removed: incoming calls always show the actual customer number
              {type.includes('WEBRTC') && (
                <div className="row g-4 mt-1">
                  <div className="col-md-8">
                    <label className="mb-1.5 block text-xs font-semibold text-foreground" style={labelStyle}>
                      Incoming Call Display (WEBRTC Only) {requiredMark}
                    </label>
                    <div className="d-flex flex-wrap gap-3" style={{ marginTop: 6, fontSize: 12 }}>
                      <label className="d-inline-flex align-items-center gap-1">
                        <input
                          type="radio"
                          name="cliOption"
                          value="actual"
                          checked={cliOption === 'actual'}
                          onChange={(e) => setCliOption(e.target.value)}
                        />
                        Show Actual Number
                      </label>
                      <label className="d-inline-flex align-items-center gap-1">
                        <input
                          type="radio"
                          name="cliOption"
                          value="virtual"
                          checked={cliOption === 'virtual'}
                          onChange={(e) => setCliOption(e.target.value)}
                        />
                        Show Virtual Number (Assigned CLI)
                      </label>
                    </div>
                  </div>
                </div>
              )} */}
            </CardContent>
          </Card>
          )}

          {stepper.current === 1 && (
          <div className="flex flex-col gap-3">
            <div>
              <Card style={cardStyle}>
                <CardContent style={{ padding: 20 }}>
                  <div className="d-flex align-items-start gap-2 mb-4">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary mt-0.5">
                      <FaCog size={14} />
                    </span>
                    <div>
                      <p className="m-0 text-sm font-bold text-foreground">Settings</p>
                      <p className="m-0 text-xs text-muted-foreground">
                        Configure additional settings for the agent.
                      </p>
                    </div>
                  </div>

                  <div className="row g-3">
                    {canVoicemailEnabled ? (
                      <div className="col-md-4">
                        <div className="d-flex align-items-center gap-1 mb-2">
                          <label className="form-label agent-form-label mb-0" style={labelStyle}>
                            Voicemail Enabled
                          </label>
                          <FaInfoCircle size={11} style={{ color: '#94a3b8' }} />
                        </div>
                        <div className="form-check form-switch" style={{ paddingTop: 2 }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="voicemailEnabled"
                            checked={!!voicemailEnabled}
                            onChange={(e) => setVoicemailEnabled(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="voicemailEnabled"
                            style={{ marginLeft: 8, fontSize: 12 }}
                          >
                            {voicemailEnabled ? 'Enabled' : 'Disabled'}
                          </label>
                        </div>
                      </div>
                    ) : null}
                    {canTranscriptionEnabled ? (
                      <div className="col-md-4">
                        <div className="d-flex align-items-center gap-1 mb-2">
                          <label className="form-label agent-form-label mb-0" style={labelStyle}>
                            Transcription Enabled
                          </label>
                          <FaInfoCircle size={11} style={{ color: '#94a3b8' }} />
                        </div>
                        <div className="form-check form-switch" style={{ paddingTop: 2 }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="transcriptionEnabled"
                            checked={!!transcriptionEnabled}
                            onChange={(e) => setTranscriptionEnabled(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="transcriptionEnabled"
                            style={{ marginLeft: 8, fontSize: 12 }}
                          >
                            {transcriptionEnabled ? 'Enabled' : 'Disabled'}
                          </label>
                        </div>
                      </div>
                    ) : null}
                    {canOutboundEnabled ? (
                      <div className="col-md-4">
                        <div className="d-flex align-items-center gap-1 mb-2">
                          <label className="form-label agent-form-label mb-0" style={labelStyle}>
                            Outbound Enabled
                          </label>
                          <FaInfoCircle size={11} style={{ color: '#94a3b8' }} />
                        </div>
                        <div className="form-check form-switch" style={{ paddingTop: 2 }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isOutboundEnabled"
                            checked={!!isOutboundEnabled}
                            onChange={(e) => setIsOutboundEnabled(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="isOutboundEnabled"
                            style={{ marginLeft: 8, fontSize: 12 }}
                          >
                            {isOutboundEnabled ? 'Enabled' : 'Disabled'}
                          </label>
                        </div>
                      </div>
                    ) : null}
                    {showAutoAnswer ? (
                      <div className="col-md-4">
                        <div className="d-flex align-items-center gap-1 mb-2">
                          <label className="form-label agent-form-label mb-0" style={labelStyle}>
                            Auto Answer
                          </label>
                          <FaInfoCircle
                            size={11}
                            style={{ color: '#94a3b8' }}
                            title="Incoming calls are answered automatically for this agent. If the agent can change it, this is the starting value."
                          />
                        </div>
                        <div className="form-check form-switch" style={{ paddingTop: 2 }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="autoAnswer"
                            checked={!!autoAnswer}
                            onChange={(e) => setAutoAnswer(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="autoAnswer"
                            style={{ marginLeft: 8, fontSize: 12 }}
                          >
                            {autoAnswer ? 'Enabled' : 'Disabled'}
                          </label>
                        </div>
                      </div>
                    ) : null}
                    {showAutoAnswer ? (
                      <div className="col-md-4">
                        <div className="d-flex align-items-center gap-1 mb-2">
                          <label className="form-label agent-form-label mb-0" style={labelStyle}>
                            Show Auto Answer to Agent
                          </label>
                          <FaInfoCircle
                            size={11}
                            style={{ color: '#94a3b8' }}
                            title="The agent sees an Auto Answer switch on their dialer and can turn it on or off"
                          />
                        </div>
                        <div className="form-check form-switch" style={{ paddingTop: 2 }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="showAutoAnswerToggle"
                            checked={!!showAutoAnswerToggle}
                            onChange={(e) => setShowAutoAnswerToggle(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="showAutoAnswerToggle"
                            style={{ marginLeft: 8, fontSize: 12 }}
                          >
                            {showAutoAnswerToggle ? 'Yes' : 'No'}
                          </label>
                        </div>
                      </div>
                    ) : null}
                    {canTransfer ? (
                      <div className="col-md-4">
                        <div className="d-flex align-items-center gap-1 mb-2">
                          <label className="form-label agent-form-label mb-0" style={labelStyle}>
                            Transfer
                          </label>
                          <FaInfoCircle
                            size={11}
                            style={{ color: '#94a3b8' }}
                            title="Show the Transfer button on the agent's dialer"
                          />
                        </div>
                        <div className="form-check form-switch" style={{ paddingTop: 2 }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="transferEnabled"
                            checked={!!transferEnabled}
                            onChange={(e) => setTransferEnabled(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="transferEnabled"
                            style={{ marginLeft: 8, fontSize: 12 }}
                          >
                            {transferEnabled ? 'Enabled' : 'Disabled'}
                          </label>
                        </div>
                      </div>
                    ) : null}
                    {canConference ? (
                      <div className="col-md-4">
                        <div className="d-flex align-items-center gap-1 mb-2">
                          <label className="form-label agent-form-label mb-0" style={labelStyle}>
                            Conference
                          </label>
                          <FaInfoCircle
                            size={11}
                            style={{ color: '#94a3b8' }}
                            title="Show the Conference button on the agent's dialer"
                          />
                        </div>
                        <div className="form-check form-switch" style={{ paddingTop: 2 }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="conferenceEnabled"
                            checked={!!conferenceEnabled}
                            onChange={(e) => setConferenceEnabled(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="conferenceEnabled"
                            style={{ marginLeft: 8, fontSize: 12 }}
                          >
                            {conferenceEnabled ? 'Enabled' : 'Disabled'}
                          </label>
                        </div>
                      </div>
                    ) : null}
                    {canEarlyFollowUp ? (
                      <div className="col-md-4">
                        <div className="d-flex align-items-center gap-1 mb-2">
                          <label className="form-label agent-form-label mb-0" style={labelStyle}>
                            Early Follow-up Call
                          </label>
                          <FaInfoCircle
                            size={11}
                            style={{ color: '#94a3b8' }}
                            title="The agent can call a follow-up before its scheduled time (only while idle)"
                          />
                        </div>
                        <div className="form-check form-switch" style={{ paddingTop: 2 }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="earlyFollowUp"
                            checked={!!earlyFollowUp}
                            onChange={(e) => setEarlyFollowUp(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="earlyFollowUp"
                            style={{ marginLeft: 8, fontSize: 12 }}
                          >
                            {earlyFollowUp ? 'Enabled' : 'Disabled'}
                          </label>
                        </div>
                      </div>
                    ) : null}
                    <div className="col-md-4">
                      <div className="d-flex align-items-center gap-1 mb-2">
                        <label className="form-label agent-form-label mb-0" style={labelStyle}>
                          Number Masking
                        </label>
                        <FaInfoCircle size={11} style={{ color: '#94a3b8' }} />
                      </div>
                      <div className="form-check form-switch" style={{ paddingTop: 2 }}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="maskingEnabled"
                          checked={!!maskingEnabled}
                          onChange={(e) => setMaskingEnabled(e.target.checked)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="maskingEnabled"
                          style={{ marginLeft: 8, fontSize: 12 }}
                        >
                          {maskingEnabled ? 'Enabled' : 'Disabled'}
                        </label>
                      </div>
                    </div>
                    {type.includes('Mobile') && (
                      <div className="col-md-4">
                        <div className="d-flex align-items-center gap-1 mb-2">
                          <label
                            htmlFor="firstCallAcceptance"
                            className="form-label agent-form-label mb-0"
                            style={labelStyle}
                          >
                            First Call Connect (MOBILE Only)
                          </label>
                          <FaInfoCircle size={11} style={{ color: '#94a3b8' }} />
                        </div>
                        <div style={inputWrapperStyle}>
                          <FaUser style={inputIconStyle} />
                          <select
                            id="firstCallAcceptance"
                            value={firstCallAcceptance || 'agent'}
                            onChange={(e) => setFirstCallAcceptance(e.target.value || 'agent')}
                            className="form-control form-select"
                            style={iconInputStyle}
                          >
                            {firstCallAcceptanceOptions.map((item) => (
                              <option key={item.value} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    <div className="col-md-12">
                      <CountryCodeDirectionPicker
                        items={availableCcDirections}
                        selectedIds={selectedCcDirections}
                        onChange={setSelectedCcDirections}
                        label="Country Codes (Optional)"
                        placeholder="Select country codes"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <Card style={cardStyle}>
                <CardContent style={{ padding: 20 }}>
                  <div className="d-flex align-items-start gap-2 mb-4">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary mt-0.5">
                      <FaFileAlt size={13} />
                    </span>
                    <div>
                      <p className="m-0 text-sm font-bold text-foreground">
                        Description <span className="ml-1 text-xs font-normal text-muted-foreground">(Optional)</span>
                      </p>
                      <p className="m-0 text-xs text-muted-foreground">Add a brief description about the agent.</p>
                    </div>
                  </div>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="form-control w-full"
                    placeholder="Enter description"
                    rows={4}
                    maxLength={500}
                    style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
                  />
                  <div className="mt-1 text-right text-xs text-muted-foreground">
                    {String(description || '').length} / 500
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          )}

          {isCallCentreAdmin && stepper.current === 2 && (
            <Card style={cardStyle} className="mb-3 mt-3">
              <CardContent style={{ padding: 20 }}>
                <AgentAccountSettingsSection
                  access={access}
                  selectedACL={selectedACL}
                  setSelectedACL={setSelectedACL}
                  accountType={accountType}
                  setAccountType={setAccountType}
                  expiryDate={expiryDate}
                  setExpiryDate={setExpiryDate}
                  parentAccountType={parentAccountType}
                  parentExpiryDate={parentExpiryDate}
                  labelStyle={labelStyle}
                  inputStyle={inputStyle}
                  iconInputStyle={iconInputStyle}
                  inputWrapperStyle={inputWrapperStyle}
                  inputIconStyle={inputIconStyle}
                  requiredMark={requiredMark}
                  disableExpiry={isDemoCompanyAgent}
                />
              </CardContent>
            </Card>
          )}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 px-2 py-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <FaLightbulb size={13} className="text-muted-foreground" />
              <span>
                Step{' '}
                <span className="font-semibold text-foreground">
                  {stepper.current + 1}
                </span>{' '}
                of{' '}
                <span className="font-semibold text-foreground">
                  {stepDescriptors.length}
                </span>{' '}
                · Fields marked with{' '}
                <span className="text-destructive">*</span> are required.
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex h-10 min-w-[100px] items-center justify-center rounded-md border border-border/60 bg-card px-4 text-xs font-semibold text-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.35),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
              >
                Cancel
              </button>
              {!stepper.isFirst && (
                <button
                  type="button"
                  onClick={stepper.prev}
                  className="inline-flex h-10 min-w-[100px] items-center justify-center gap-2 rounded-md border border-border/60 bg-card px-4 text-xs font-semibold text-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.35),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
                >
                  <FaChevronLeft size={11} />
                  Previous
                </button>
              )}
              {!stepper.isLast && (
                <button
                  type="button"
                  onClick={() => {
                    if (stepper.current === 0) {
                      const nameErr = getAgentNameFieldError(agentName, existingAgentNamesRef.current);
                      if (nameErr) {
                        setAgentNameError(nameErr);
                        toast.error(nameErr);
                        return;
                      }
                      if (!validateUsername(username)) {
                        toast.error(usernameError || 'Username validation failed');
                        return;
                      }
                      if (!isEditMode && createDefaultExtension) {
                        if (extensionLocalError) {
                          toast.error(extensionLocalError);
                          return;
                        }
                        if (extensionLocal && !EXTENSION_LOCAL_VALID.test(extensionLocal)) {
                          toast.error(EXTENSION_LOCAL_HINT);
                          return;
                        }
                        if (!(type.includes('Soft Phone') || type.includes('WEBRTC'))) {
                          toast.error(
                            'Select Soft Phone or WEBRTC to use a default extension.',
                          );
                          return;
                        }
                      }
                    }
                    stepper.next();
                  }}
                  className="inline-flex h-10 min-w-[100px] items-center justify-center gap-2 rounded-md border border-primary bg-primary px-4 text-xs font-bold text-primary-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.4),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-primary/90 cursor-pointer"
                >
                  Next
                  <FaChevronRight size={11} />
                </button>
              )}
              {stepper.isLast && (
                <button
                  type="button"
                  onClick={handleSaveAgent}
                  disabled={loading}
                  className="inline-flex h-10 min-w-[124px] items-center justify-center gap-2 rounded-md border border-primary bg-primary px-4 text-xs font-bold text-primary-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.4),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                >
                  <FaPlusCircle size={14} />
                  {loading
                    ? isEditMode
                      ? 'Updating…'
                      : 'Creating…'
                    : isEditMode
                      ? 'Update Agent'
                      : 'Create Agent'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      <Dialog
        open={showExtensionConfirm}
        onOpenChange={(next) => {
          if (!next) {
            setShowExtensionConfirm(false);
            setPendingExtension(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-base">Confirm Extension</DialogTitle>
          </DialogHeader>
          <div className="text-sm">
            Are you sure you want to proceed with this extension?
            {pendingExtension && (
              <div className="mt-2 font-semibold">
                Selected Extension:{' '}
                <span className="text-xl font-medium">
                  {extensionOptions.find((ext) => String(ext.value) === String(pendingExtension))?.label ||
                    pendingExtension}
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowExtensionConfirm(false);
                setPendingExtension(null);
              }}
            >
              No
            </Button>
            <Button
              onClick={() => {
                setSelectedExtension(pendingExtension);
                setShowExtensionConfirm(false);
                setPendingExtension(null);
              }}
            >
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ScrollPage>
  );
}
